import { useState, useEffect, useRef } from "react";
import "../housestyle.css";
import "./Ranks.css";
import * as Icons from "../Icons/Icons";
import api from "../API/Api";
import PageHero from "../components/PageHero";
import { McIcon } from "../components/CraftingGrid";
import { FadeIn, StaggerContainer, StaggerItem } from "../components/Animations";
import { useAuth } from "../components/AuthContext";

// Cumulative ladder — each rank includes everything before it, so owning "fire" means
// earth/wind/water are already covered too. Order matches RANK_META's tiers.
const RANK_ORDER = ["earth", "wind", "water", "fire", "titan"];

function rankIndex(name) {
  return RANK_ORDER.indexOf((name || "").toLowerCase());
}

// Shows a struck-through original price beside the discounted one when an upgrade credit applies
function PriceTag({ full, price, className = "" }) {
  if (price < full) {
    return (
      <>
        <span className={`rankx-price-old ${className}`}>${full.toFixed(2)}</span>
        <span className={`rankx-price-now ${className}`}>${price.toFixed(2)}</span>
      </>
    );
  }
  return <span className={`rankx-price-now ${className}`}>${full.toFixed(2)}</span>;
}

const HERO_VIDEO = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_3.mp4';

// Elemental rank theming — keyed by lowercase rank name from the DB
const RANK_META = {
  earth: { color: '#7cba46', color2: '#4e8c2f', material: 'moss_block',       tier: 'Tier I',   tagline: 'Rooted & steady' },
  wind:  { color: '#8fd8e8', color2: '#5fb8d4', material: 'breeze_rod',       tier: 'Tier II',  tagline: 'Swift & free' },
  water: { color: '#4a9ee0', color2: '#2f6fc4', material: 'heart_of_the_sea', tier: 'Tier III', tagline: 'Deep & relentless' },
  fire:  { color: '#e8642c', color2: '#c23a18', material: 'blaze_powder',     tier: 'Tier IV',  tagline: 'Burn brighter' },
  titan: { color: '#f0ab20', color2: '#ffd766', material: 'nether_star',      tier: 'Ultimate', tagline: 'Become unstoppable' },
};

// Legacy mob-rank icons — still render if the DB has old rows
const RANK_ICONS = {
  zombie: Icons.zombie,
  skeleton: Icons.skeleton,
  creeper: Icons.creeper,
  blaze: Icons.blaze,
  breeze: Icons.breeze,
  phantom: Icons.phantom,
  warden: Icons.warden,
};

// Fallback so the page renders the new lineup even before the DB is updated
const DEFAULT_RANKS = [
  { id: 'earth', name: 'Earth', price: 4.99, discount_percent: 0, perks: [
    'Earth prefix in chat & tab', '3 /sethome slots', '1 personal vault', 'Chat colors', 'Daily reward crate',
  ] },
  { id: 'wind', name: 'Wind', price: 9.99, discount_percent: 0, perks: [
    'Everything in Earth', 'Wind prefix', '5 /sethome slots', '2 personal vaults', '/hat & /nick', 'Exclusive kill messages',
  ] },
  { id: 'water', name: 'Water', price: 16.99, discount_percent: 0, perks: [
    'Everything in Wind', 'Water prefix', '8 /sethome slots', '4 personal vaults', 'Custom suffix', 'Exclusive emotes', '/craft & /enderchest',
  ] },
  { id: 'fire', name: 'Fire', price: 24.99, discount_percent: 0, perks: [
    'Everything in Water', 'Fire prefix', '12 /sethome slots', '6 personal vaults', 'Priority queue', 'Fire particle trail', 'Monthly bonus crate',
  ] },
  { id: 'titan', name: 'Titan', price: 39.99, discount_percent: 0, perks: [
    'Everything in Fire', 'Titan prefix', 'Unlimited /sethome slots', '9 personal vaults', 'Titan particle trail', 'First access to playtests', 'Exclusive Titan cosmetics',
  ] },
];

const RANK_BADGES = {
  fire: { label: 'Most Popular', className: 'popular' },
  titan: { label: 'Best Value', className: 'value' },
};

// Compare table — values keyed by lowercase rank name
const COMPARE_FEATURES = [
  { label: 'Homes',            values: { earth: '3', wind: '5', water: '8', fire: '12', titan: 'Unlimited' } },
  { label: 'Vault Slots',      values: { earth: '1', wind: '2', water: '4', fire: '6', titan: '9' } },
  { label: 'Chat Colors',      values: { earth: true, wind: true, water: true, fire: true, titan: true } },
  { label: 'Custom Suffix',    values: { earth: false, wind: false, water: true, fire: true, titan: true } },
  { label: 'Exclusive Emotes', values: { earth: false, wind: false, water: true, fire: true, titan: true } },
  { label: 'Priority Queue',   values: { earth: false, wind: false, water: false, fire: true, titan: true } },
  { label: 'Particle Trails',  values: { earth: false, wind: false, water: false, fire: true, titan: true } },
  { label: 'Daily Rewards',    values: { earth: true, wind: true, water: true, fire: true, titan: true } },
];

function rankMeta(rank) {
  return RANK_META[rank.name.toLowerCase()] || {
    color: rank.color || '#9e9e9e',
    color2: rank.color || '#9e9e9e',
    material: 'barrier',
    tier: '',
    tagline: '',
  };
}

function RankIcon({ rank, size = 56 }) {
  const legacy = RANK_ICONS[rank.name.toLowerCase()];
  if (legacy) return <img src={legacy} alt={rank.name} className="rank-icon" style={{ width: size, height: size }} />;
  return <McIcon materialKey={rankMeta(rank).material} size={size} />;
}

// Tebex Headless API public token (Creator Panel -> Integrations -> API Keys -> Public Token)
window.TEBEX_PUBLIC_KEY = "13wz7-a6ec6cd3b3becfb36271dfbdc49e9528a99162f1";

// Shared Tebex Headless checkout — creates a basket for `username`, adds `packageId`,
// and returns the hosted checkout URL. Used by both the rank modal and cosmetic items.
async function createTebexCheckout(username, packageId) {
  const basketRes = await fetch(`https://headless.tebex.io/api/accounts/${window.TEBEX_PUBLIC_KEY}/baskets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      complete_url: window.location.origin + "/store/success",
      cancel_url: window.location.origin + "/store/cancel",
      username,
    }),
  });
  if (!basketRes.ok) throw new Error("Failed to create checkout basket");
  const basketData = await basketRes.json();
  const basketIdent = basketData.data.ident;

  const packageRes = await fetch(`https://headless.tebex.io/api/baskets/${basketIdent}/packages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ package_id: packageId, quantity: 1 }),
  });
  if (!packageRes.ok) throw new Error("Failed to add item to basket");
  const packageData = await packageRes.json();
  return packageData.data.links.checkout;
}

// Tebex package IDs per rank (map is the fallback; DB tebex_id wins).
const RANK_TEBEX = {
  earth: 7570981,
  wind: 7574132,
  water: 7574133,
  fire: 7574136,
  titan: 7574159,
};

// Store is still in testing — only these staff roles get a real, working checkout
// button anywhere in the store (ranks, kill effects, join messages). Everyone else
// sees a disabled "Coming Soon" button instead, so no live purchase flow (and no
// Tebex checkout link) is ever exposed to regular players before launch.
const STORE_TEST_STAFF = ["admin", "manager", "owner"];
function canUseLiveStore(user) {
  return !!user?.staff && STORE_TEST_STAFF.includes(user.staff.toLowerCase());
}

function BuyModal({ rank, saleCode, onClose, allRanks, forceGift }) {
  const { user, requestLogin } = useAuth();
  const [copied, setCopied] = useState(false);
  const [giftMode, setGiftMode] = useState(forceGift);
  const [giftIgn, setGiftIgn] = useState("");
  const [giftLookup, setGiftLookup] = useState(null); // null | {status:'checking'|'found'|'notfound', ign, rank}
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const pct = rank.discount_percent;
  const showCode = saleCode && pct;
  const meta = rankMeta(rank);
  const displayName = rank.name.charAt(0).toUpperCase() + rank.name.slice(1);

  // Who this purchase applies to - the logged-in player themself, or a validated gift target
  const targetUsername = !user
    ? null
    : giftMode
      ? (giftLookup?.status === "found" ? giftLookup.ign : null)
      : user.ign;

  // Upgrade credit: full price minus whatever the buyer (or gift recipient) already paid
  // for their current rank, so nobody re-pays for perks they already have.
  const fullPrice = Number(rank.price) || 0;
  const thisIdx = rankIndex(rank.name);
  function priceOf(rankName) {
    const r = (allRanks || []).find(x => x.name.toLowerCase() === (rankName || "").toLowerCase());
    return r ? Number(r.price) || 0 : 0;
  }
  const selfOwnedIdx = rankIndex(user?.rank);
  const selfPrice = (selfOwnedIdx >= 0 && thisIdx > selfOwnedIdx)
    ? Math.max(fullPrice - priceOf(user.rank), 0)
    : fullPrice;
  const giftOwnedIdx = giftLookup?.status === "found" ? rankIndex(giftLookup.rank) : -1;
  const giftPrice = (giftOwnedIdx >= 0 && thisIdx > giftOwnedIdx)
    ? Math.max(fullPrice - priceOf(giftLookup.rank), 0)
    : fullPrice;
  const shownPrice = giftMode ? giftPrice : selfPrice;

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Debounced IGN lookup - checks the gift target has actually played the server
  useEffect(() => {
    if (!giftMode || giftIgn.trim().length < 3) {
      setGiftLookup(null);
      return;
    }
    setGiftLookup({ status: "checking" });
    const handle = setTimeout(async () => {
      const res = await api("playerlookupign", { ign: giftIgn.trim() });
      if (res.isSuccess && res.data) {
        setGiftLookup({ status: "found", ign: res.data.ign, rank: res.data.rank });
      } else {
        setGiftLookup({ status: "notfound", message: res.message || "Player not found." });
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [giftIgn, giftMode]);

  function copy() {
    navigator.clipboard.writeText(saleCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleCheckout() {
    if (!targetUsername) {
      setCheckoutError(giftMode ? "Enter a valid player to gift this to." : "Please log in first.");
      return;
    }

    setIsCheckingOut(true);
    setCheckoutError("");

    try {
      const packageId = rank.tebex_id || RANK_TEBEX[rank.name.toLowerCase()];
      if (!packageId) throw new Error("Tebex Package ID not found for this rank.");
      const checkoutUrl = await createTebexCheckout(targetUsername, packageId);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error(err);
      setCheckoutError(err.message || "An error occurred during checkout.");
      setIsCheckingOut(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div
        className="modal-box"
        style={{ '--rk': meta.color, '--rk2': meta.color2 }}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="modal-header">
          <div className="modal-orb">
            <RankIcon rank={rank} size={52} />
          </div>
          <div>
            {meta.tier && <span className="modal-tier">{meta.tier}</span>}
            <div className="modal-rank-name">
              {forceGift ? `Gift ${displayName}` : displayName}
            </div>
            <div className="modal-price-row">
              <span className="rankx-price-now">${fullPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="modal-perks-label">Included perks</div>
        <div className="modal-perks">
          {rank.perks.map((p) => (
            <div key={p} className="modal-perk">
              <span className="modal-perk-check">✓</span>
              {p}
            </div>
          ))}
        </div>

        {showCode && (
          <div className="modal-code-row">
            <span className="modal-code-label">Use code at checkout:</span>
            <button className="modal-code-btn" onClick={copy}>
              <span className="modal-code-text">{saleCode}</span>
              <span className="modal-code-copy">
                {copied ? "Copied!" : "Copy"}
              </span>
            </button>
          </div>
        )}

        {!user ? (
          <div className="modal-login-gate">
            <p>Log in with your Minecraft account to buy or gift this rank.</p>
            <button
              type="button"
              className="modal-buy-btn"
              onClick={() => { requestLogin(); onClose(); }}
            >
              Log In
            </button>
          </div>
        ) : (
          <div className="modal-checkout-section" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {forceGift ? (
              <div className="modal-gift-only-note">
                🎁 You already own this rank — gift it to a friend instead.
              </div>
            ) : (
              <div className="modal-buyer-toggle">
                <button
                  type="button"
                  className={`modal-buyer-tab${!giftMode ? ' active' : ''}`}
                  onClick={() => setGiftMode(false)}
                >
                  For Myself
                </button>
                <button
                  type="button"
                  className={`modal-buyer-tab${giftMode ? ' active' : ''}`}
                  onClick={() => setGiftMode(true)}
                >
                  🎁 Gift to Someone
                </button>
              </div>
            )}

            {!giftMode ? (
              <div className="modal-buyer-self">
                <img src={user.avatar} alt={user.ign} className="modal-buyer-avatar" />
                <span>Buying for <strong>{user.ign}</strong></span>
              </div>
            ) : (
              <>
                <label className="modal-ign-label">Gift to (Minecraft username)</label>
                <input
                  type="text"
                  className="modal-ign-input"
                  placeholder="Notch"
                  value={giftIgn}
                  onChange={(e) => setGiftIgn(e.target.value)}
                  maxLength={16}
                />
                {giftLookup?.status === "checking" && (
                  <div className="modal-ign-status checking">Checking…</div>
                )}
                {giftLookup?.status === "found" && (
                  <div className="modal-ign-status found">
                    <img src={`https://mc-heads.net/avatar/${giftLookup.ign}/32`} alt={giftLookup.ign} />
                    Gifting to <strong>{giftLookup.ign}</strong>
                    {giftOwnedIdx >= 0 && giftOwnedIdx < thisIdx && (
                      <span className="modal-ign-upgrade-note">already has {giftLookup.rank} — upgrade credit applied</span>
                    )}
                  </div>
                )}
                {giftLookup?.status === "notfound" && (
                  <div className="modal-ign-status notfound">{giftLookup.message}</div>
                )}
              </>
            )}

            {targetUsername && (
              <div className="modal-price-summary">
                <span>Total</span>
                <PriceTag full={fullPrice} price={shownPrice} />
              </div>
            )}

            {checkoutError && <div style={{ color: 'var(--c-danger)', fontSize: '14px' }}>{checkoutError}</div>}
          </div>
        )}

        {user && (
          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button className="btn modal-cancel-btn" onClick={onClose} disabled={isCheckingOut}>
              Cancel
            </button>
            {canUseLiveStore(user) ? (
              <button
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="modal-buy-btn"
                style={{ opacity: isCheckingOut ? 0.7 : 1, cursor: isCheckingOut ? 'not-allowed' : 'pointer' }}
              >
                {isCheckingOut ? "Processing..." : giftMode ? "Gift Rank" : "Checkout"}
              </button>
            ) : (
              <button className="modal-buy-btn" disabled style={{ opacity: 0.55, cursor: 'not-allowed' }}>
                Coming Soon
              </button>
            )}
          </div>
        )}

        <p className="modal-disclaimer">
          Ranks are permanent and carry over between seasons. No refunds after
          rank has been applied.
        </p>
      </div>
    </div>
  );
}

function RankCard({ rank, onBuy, onGift, ownedIndex, ownedPrice }) {
  const { user } = useAuth();
  const storeLive = canUseLiveStore(user);
  const meta = rankMeta(rank);
  const displayName = rank.name.charAt(0).toUpperCase() + rank.name.slice(1);
  const badge = RANK_BADGES[rank.name.toLowerCase()];
  const isTitan = rank.name.toLowerCase() === 'titan';
  const thisIndex = rankIndex(rank.name);
  const isCurrent = ownedIndex >= 0 && thisIndex === ownedIndex;
  const isIncluded = ownedIndex >= 0 && thisIndex < ownedIndex;
  const isOwned = isCurrent || isIncluded;
  const isUpgrade = ownedIndex >= 0 && thisIndex > ownedIndex;
  const price = Number(rank.price) || 0;
  const shownPrice = isUpgrade ? Math.max(price - ownedPrice, 0) : price;

  // spotlight follows the cursor inside the card
  function onMove(e) {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  }

  return (
    <div
      className={`rankx-card${isTitan ? ' rankx-card--titan' : ''}${isCurrent ? ' rankx-card--owned' : ''}`}
      style={{ '--rk': meta.color, '--rk2': meta.color2 }}
      onMouseMove={onMove}
    >
      <div className="rankx-spotlight" aria-hidden="true" />
      <div className="rankx-embers" aria-hidden="true">
        <span /><span /><span /><span /><span />
      </div>

      {isCurrent ? (
        <span className="rankx-badge current">★ Current Rank</span>
      ) : (
        badge && <span className={`rankx-badge ${badge.className}`}>{badge.label}</span>
      )}

      <div className="rankx-bigname" aria-hidden="true">{displayName}</div>

      <div className="rankx-orb">
        <RankIcon rank={rank} size={54} />
      </div>

      <span className="rankx-tier">{meta.tier}</span>
      <h3 className="rankx-name">{displayName}</h3>
      {meta.tagline && <span className="rankx-tagline">{meta.tagline}</span>}

      {!isOwned && (
        <div className="rankx-price">
          <PriceTag full={price} price={shownPrice} />
          {isUpgrade && <span className="rankx-price-note">Upgrade</span>}
        </div>
      )}

      <div className="rankx-perks">
        {rank.perks.map((p) => (
          <div key={p} className="rankx-perk">
            <span className="rankx-perk-check">✓</span>
            {p}
          </div>
        ))}
      </div>

      {isOwned ? (
        <>
          <button className="rankx-buy rankx-buy--owned" disabled>
            {isCurrent ? "✓ Owned" : "✓ Included"}
          </button>
          {storeLive && (
            <button type="button" className="rankx-gift-link" onClick={() => onGift(rank)}>
              Gift this rank
            </button>
          )}
        </>
      ) : storeLive ? (
        <button className="rankx-buy" onClick={() => onBuy(rank)}>
          {isUpgrade ? `Upgrade to ${displayName}` : `Get ${displayName}`}
        </button>
      ) : (
        <button className="rankx-buy rankx-buy--soon" disabled>
          Coming Soon
        </button>
      )}
    </div>
  );
}

// horizontal element progression rail above the cards
function ElementRail({ ranks }) {
  if (ranks.length < 2) return null;
  const gradient = `linear-gradient(90deg, ${ranks.map(r => rankMeta(r).color).join(', ')})`;
  return (
    <div className="rankx-rail" aria-hidden="true">
      <div className="rankx-rail-line" style={{ background: gradient }} />
      {ranks.map(r => {
        const m = rankMeta(r);
        return (
          <div key={r.id} className="rankx-rail-node" style={{ '--rk': m.color }}>
            <div className="rankx-rail-orb">
              <McIcon materialKey={m.material} size={20} />
            </div>
            <span className="rankx-rail-name">{r.name}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MINECRAFT FORMAT-CODE PARSER
   Walks the string left→right. A color code (&0-&f) or &#RRGGBB sets the
   current color AND resets formatting (vanilla MC behaviour); &l/&o/&n/&m
   add a format; &r resets everything. {player} is substituted first.
   ═══════════════════════════════════════════════════════════════════ */
const MC_HEX = {
  "0": "#000000", "1": "#0000AA", "2": "#00AA00", "3": "#00AAAA",
  "4": "#AA0000", "5": "#AA00AA", "6": "#FFAA00", "7": "#AAAAAA",
  "8": "#555555", "9": "#5555FF", a: "#55FF55", b: "#55FFFF",
  c: "#FF5555", d: "#FF55FF", e: "#FFFF55", f: "#FFFFFF",
};

function parseMcChat(raw, player) {
  const text = String(raw).replace(/\{player\}/g, player);
  const segments = [];
  let color = "#ffffff";
  let bold = false, italic = false, underline = false, strike = false;
  let buf = "";

  const flush = () => {
    if (buf) segments.push({ text: buf, color, bold, italic, underline, strike });
    buf = "";
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if ((ch === "&" || ch === "§") && i + 1 < text.length) {
      // &#RRGGBB — exact hex colour
      if (text[i + 1] === "#" && /^[0-9a-fA-F]{6}$/.test(text.slice(i + 2, i + 8))) {
        flush();
        color = "#" + text.slice(i + 2, i + 8);
        bold = italic = underline = strike = false;
        i += 7;
        continue;
      }
      const code = text[i + 1].toLowerCase();
      if (MC_HEX[code]) { flush(); color = MC_HEX[code]; bold = italic = underline = strike = false; i++; continue; }
      if (code === "l") { flush(); bold = true; i++; continue; }
      if (code === "o") { flush(); italic = true; i++; continue; }
      if (code === "n") { flush(); underline = true; i++; continue; }
      if (code === "m") { flush(); strike = true; i++; continue; }
      if (code === "r") { flush(); color = "#ffffff"; bold = italic = underline = strike = false; i++; continue; }
    }
    buf += ch;
  }
  flush();
  return segments;
}

function McChat({ text, player }) {
  const segs = parseMcChat(text, player);
  return (
    <span className="mc-chat-line">
      {segs.map((s, i) => (
        <span
          key={i}
          style={{
            color: s.color,
            fontWeight: s.bold ? 700 : undefined,
            fontStyle: s.italic ? "italic" : undefined,
            textDecoration: [s.underline && "underline", s.strike && "line-through"]
              .filter(Boolean).join(" ") || undefined,
          }}
        >
          {s.text}
        </span>
      ))}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   KILL EFFECTS — 13 standalone premium, 5 rank-tied.
   `fx` picks the decorative looping animation; `color` accents the card.
   ═══════════════════════════════════════════════════════════════════ */
// Preview clips live on the same R2 CDN as the site trailer. Set `video: true`
// on an effect to serve `${KILLFX_VIDEO_BASE}/<key>.mp4`, or give `video` a full
// URL to point elsewhere. `audio: true` flags effects that ship with custom sound.
const KILLFX_VIDEO_BASE = "https://pub-cc36484e217444048e2452df62aea1b0.r2.dev";

// `video: true` → `<key>.mp4` on the bucket. A string is either a full URL or a
// bare object name — the uploads are named for display ("Chicken Riot.mp4"), not
// by key, so spaces get encoded here rather than being baked into the data.
function effectAsset(value, fallbackName) {
  if (value === true) return `${KILLFX_VIDEO_BASE}/${encodeURIComponent(fallbackName)}`;
  if (/^https?:\/\//.test(value)) return value;
  return `${KILLFX_VIDEO_BASE}/${encodeURIComponent(value)}`;
}

function effectVideo(effect) {
  if (!effect.video) return null;
  return effectAsset(effect.video, `${effect.key}.mp4`);
}

// Poster = one exported frame per effect, ~40KB. Phones and iPads get ONLY this;
// the clip itself is never fetched until the visitor taps into the lightbox.
// Defaults to `<key>.jpg` on the killfx bucket — pass a string to override.
function effectPoster(effect) {
  if (effect.poster === false) return null;
  return effectAsset(effect.poster ?? true, `${effect.key}.jpg`);
}

// True only for real pointing devices. Touch screens report `hover: none` /
// `pointer: coarse`, which correctly catches iPads — screen width does not
// (an iPad Pro is wider than plenty of laptops).
function useCanHover() {
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setCanHover(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return canHover;
}

// All the card clips run off one shared clock so the grid beats together instead
// of each row drifting into its own rhythm. Cards mount at different moments
// (lazy loading), so a newly loaded video seeks to where the clock already is
// rather than starting at zero.
const FX_CLOCK_START = Date.now();

function syncToGridClock(video) {
  const len = video.duration;
  if (!Number.isFinite(len) || len <= 0) return;
  const target = ((Date.now() - FX_CLOCK_START) / 1000) % len;
  // Only correct real drift — seeking on every tick would stutter playback.
  if (Math.abs(video.currentTime - target) > 0.25) {
    try { video.currentTime = target; } catch { /* not seekable yet */ }
  }
}

// Desktop still shouldn't pull all 13 clips at once — only the cards actually
// scrolled near the viewport start loading.
function useInView(ref, enabled) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enabled) { setInView(false); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver !== "function") { setInView(true); return undefined; }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(e => e.isIntersecting)) {
          setInView(true);
          observer.disconnect(); // once loaded, keep it loaded
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return inView;
}

function fmtEur(n) { return `€${n.toFixed(2)}`; }

// Rarity drives both the accent shown on a card and its price. `order` sorts the
// grid legendary-first. Every kill effect is a paid premium cosmetic; `rank`-tagged
// ones are ALSO unlocked free by owning that rank (or higher).
const RARITY = {
  uncommon:  { label: 'Uncommon',  color: '#5fd45f', price: 0.99, order: 0 },
  rare:      { label: 'Rare',      color: '#4a9ee0', price: 1.49, order: 1 },
  epic:      { label: 'Epic',      color: '#b16fe8', price: 1.99, order: 2 },
  legendary: { label: 'Legendary', color: '#f0932b', price: 2.99, order: 3 },
};
function rarityOf(effect) { return RARITY[effect.rarity] || RARITY.rare; }
function effectPrice(effect) { return rarityOf(effect).price; }
const KILLFX_MIN_PRICE = Math.min(...Object.values(RARITY).map(r => r.price));

// Tebex package IDs per effect key — fill in from the Creator Panel. Until an
// effect has one (or a `tebex_id`), its buy button explains it isn't live yet.
const KILLFX_TEBEX = {};

// Clean sheet: 5 rank-tied + 13 standalone. `video: true` only where a clip is
// already on the CDN — the rest show the "No video found" placeholder until one
// is uploaded under `${KILLFX_VIDEO_BASE}/<key>.mp4`.
const KILL_EFFECTS = [
  // ── Rank-tied — free with the matching rank, otherwise buyable ──
  // Earth: ground cracks outward in a ring, stone/dirt block-crack particles erupt, deep rumble.
  { key: 'earthshatter',   name: 'Earthquake',     color: '#7cba46', fx: 'shatter',  rank: 'earth', video: 'Earthquake.mp4', rarity: 'uncommon' },
  // Wind: the drop is sucked into a white cloud vortex, lifted, then popped. Whoosh + chime.
  { key: 'cyclones_toll',  name: 'Tornado',        color: '#8fd8e8', fx: 'swirl',    rank: 'wind',  video: "Cyclone's Toll.mp4", rarity: 'rare' },
  // Water: whirlpool at the feet, bubble columns, dolphin/drowned ambience.
  { key: 'abyssal_claim',  name: 'Whirlpool',      color: '#4a9ee0', fx: 'rise',     rank: 'water', video: 'Whirlpool.mp4', rarity: 'rare' },
  // Fire: body burns into rising embers with ash falling back down. Ignite + crackle.
  { key: 'cinderfall',     name: 'Firenado',       color: '#e8642c', fx: 'fall',     rank: 'fire',  video: 'Cinderfall.mp4', rarity: 'epic' },
  // Titan: a giant golden fist slams down, shockwave ring + gold dust. Heavy boom.
  { key: 'titanfall',      name: 'Titanfall',      color: '#f0ab20', fx: 'burst',    rank: 'titan', video: 'Titanfall.mp4', rarity: 'legendary' },

  // ── Keepers ──
  // Squeaky rubber duck pops out of the death spot.
  { key: 'duck',           name: 'Rubber Duck',    color: '#ffd23f', fx: 'confetti', video: 'duck.mp4', audio: true, rarity: 'epic' },
  // A real anvil falls from the sky, lands dead-centre on the death location, clangs. No damage.
  { key: 'anvil_drop',     name: 'Anvil',          color: '#9aa0a6', fx: 'fall',     video: 'Anvil_Drop.mp4', rarity: 'rare' },
  // Eggs are hurled in from random directions toward the death spot. No chickens ever spawn.
  { key: 'chicken_riot',   name: 'Chicken Riot',   color: '#f2e9c4', fx: 'rain',     video: 'Chicken Riot.mp4', rarity: 'uncommon' },

  // ── Standalone premium effects ──
  // Lit TNT appears, fuse hisses… then just confetti and a party horn. No damage.
  { key: 'tnt_prank',      name: 'TNT Prank',      color: '#e2483c', fx: 'confetti', video: 'TNT Prank.mp4', rarity: 'rare' },
  // A vending machine drops in, thunks, and dispenses a can onto the loot.
  { key: 'vending_machine',name: 'Slot Machine',   color: '#3fa8e8', fx: 'burst',    video: 'VendingMachine.mp4', rarity: 'rare' },
  // Falling grand piano with a descending glissando and a crash landing.
  // Ivory rather than piano-black — the accent tints the card's button text, so
  // a near-black colour renders it unreadable.
  { key: 'piano_fall',     name: 'Piano Drop',     color: '#ded3c0', fx: 'fall',     video: 'Piano Fall.mp4', rarity: 'uncommon' },
  // Water swirls the drop down a drain, ending in a flush.
  { key: 'toilet_flush',   name: 'Toilet Flush',   color: '#6fd0e0', fx: 'swirl',    video: 'Toilet Flush.mp4', rarity: 'uncommon' },
  // Angry bee particles swarm out and disperse, buzzing hard.
  { key: 'bee_swarm',      name: 'Bee Swarm',      color: '#f0c419', fx: 'swirl',    video: 'Bee Swarm.mp4', rarity: 'epic' },
  // Chained coloured fireworks launch from the body into a finale burst.
  { key: 'firework_funeral',name: 'Fireworks',      color:'#ff7ab8', fx: 'burst',   video: 'Firework Funeral.mp4', rarity: 'rare' },
  // Gold nuggets rain down over the drop with a ka-ching.
  { key: 'golden_rain',    name: 'Gold Rain',      color: '#f5c542', fx: 'rain',     video: 'Golden Rain.mp4', rarity: 'rare' },
  // Soul-fire wisps rise and fly toward the killer.
  { key: 'soul_harvest',   name: 'Soul Steal',     color: '#4fd6c8', fx: 'rise',     video: 'Soul Harvest.mp4', rarity: 'epic' },
  // Bolt strike straight down with a thunderclap.
  { key: 'lightning_execution', name: 'Lightning',            color: '#f5d442', fx: 'flash',    video: 'Lightning.mp4', rarity: 'uncommon' },
  // Flaming meteor drops from the sky and craters the ground.
  { key: 'meteor_strike',  name: 'Meteor',         color: '#e8642c', fx: 'fall',     video: 'Meteor Strike.mp4', rarity: 'epic' },
  // Void sphere collapses everything inward, then implodes with a pop.
  { key: 'blackhole_pulse',name: 'Black Hole',     color: '#a98ae8', fx: 'collapse', video: 'Blackhole.mp4', rarity: 'legendary' },
]
  // Show the grid rarest-first. Stable within a tier, so the hand-ordered
  // lineup above still decides ties.
  .sort((a, b) => rarityOf(b).order - rarityOf(a).order);

function killEffectOwned(effect, ownedIndex) {
  const reqIdx = effect.rank ? rankIndex(effect.rank) : -1;
  return reqIdx >= 0 && ownedIndex >= reqIdx;
}

function KillEffectCard({ effect, ownedIndex, onOpen, onOpenRank }) {
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const owned = killEffectOwned(effect, ownedIndex);
  const rankLabel = effect.rank ? effect.rank.charAt(0).toUpperCase() + effect.rank.slice(1) : null;
  const rarity = rarityOf(effect);
  const videoUrl = effectVideo(effect);
  const posterUrl = effectPoster(effect);

  // Phones and iPads never autoplay the grid — that would cost them ~50MB of
  // clips they didn't ask for. Desktop autoplays; touch instead shows a blurred
  // still frame (metadata only, no full download) with a play button, and
  // tapping opens the lightbox, which is the only place the clip fully loads.
  const stageRef = useRef(null);
  const canHover = useCanHover();
  const inView = useInView(stageRef, canHover);
  const playInline = canHover && inView && videoUrl && !videoFailed;
  const showTouchPreview = !canHover && videoUrl && !videoFailed;

  // Decoders drift apart over time (and pause entirely in background tabs), so
  // nudge every few seconds instead of trusting the initial seek to hold.
  const videoRef = useRef(null);
  useEffect(() => {
    if (!playInline) return undefined;
    const id = setInterval(() => {
      if (videoRef.current) syncToGridClock(videoRef.current);
    }, 4000);
    return () => clearInterval(id);
  }, [playInline]);

  const showPoster = posterUrl && !posterFailed && !videoReady && !showTouchPreview;
  const showNoVideo = (!videoUrl || videoFailed) && (!posterUrl || posterFailed) && !showTouchPreview;

  function open() { onOpen(effect); }

  return (
    <div
      className={`fx-card${effect.rank ? ' fx-card--exclusive' : ''}${owned ? ' fx-card--owned' : ''}`}
      style={{ '--fx': effect.color }}
    >
      <div
        ref={stageRef}
        className="fx-stage fx-stage--clickable"
        onClick={open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        aria-label={`Preview ${effect.name} in fullscreen`}
      >
        {showPoster && (
          <img
            className="fx-poster"
            src={posterUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setPosterFailed(true)}
          />
        )}
        {playInline && (
          <video
            ref={videoRef}
            className={`fx-video${videoReady ? ' is-ready' : ''}`}
            src={videoUrl}
            muted
            loop
            autoPlay
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => syncToGridClock(e.currentTarget)}
            onCanPlay={(e) => { syncToGridClock(e.currentTarget); setVideoReady(true); }}
            onError={() => { setVideoReady(false); setVideoFailed(true); }}
          />
        )}
        {showTouchPreview && (
          <>
            <video
              className="fx-video-preview"
              // Seek to mid-effect (~2s; kills land ~1s in) so each card shows its
              // OWN effect. At 0.1s every clip is just the identical pre-kill arena.
              // Never plays inline — the play button opens the modal.
              src={`${videoUrl}#t=2`}
              muted
              playsInline
              preload="metadata"
              tabIndex={-1}
              aria-hidden="true"
              onError={() => setVideoFailed(true)}
            />
            <span className="fx-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </>
        )}
        {showNoVideo && !videoReady && (
          <div className="fx-novideo" aria-hidden="true">No video found</div>
        )}
        {effect.audio && (
          <span className="fx-audio-tag" aria-hidden="true">Custom Audio</span>
        )}
        <span className="fx-rarity" style={{ '--rar': rarity.color }}>{rarity.label}</span>
        <span className="fx-expand" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
            <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
          </svg>
        </span>
      </div>

      {/* Name + ownership on the left, one action on the right. The button always
          opens the lightbox — buying happens in there, so "Watch" is what it
          actually does for owners and buyers alike. */}
      <div className="fx-body">
        <div className="fx-info">
          <div className="fx-name">{effect.name}</div>
          <div className="fx-status">
            {owned ? (
              <span className="fx-status-own fx-status-own--yes">✓ Owned</span>
            ) : (
              <span className="fx-status-own">Not owned</span>
            )}
            <span className="fx-status-sep" aria-hidden="true">·</span>
            <span className="fx-status-src">
              {effect.rank ? (
                <>
                  Comes with{' '}
                  <button
                    type="button"
                    className="fx-rank-link"
                    style={{ '--rk': RANK_META[effect.rank]?.color || effect.color }}
                    onClick={() => onOpenRank?.(effect.rank)}
                  >
                    {rankLabel} Rank
                  </button>
                </>
              ) : 'Premium'}
            </span>
          </div>
        </div>

        <button className={`fx-btn ${owned ? 'fx-btn--watch' : 'fx-btn--buy'}`} onClick={open}>
          {owned ? 'Watch' : `Watch / Buy · ${fmtEur(effectPrice(effect))}`}
        </button>
      </div>
    </div>
  );
}

// Fullscreen preview player + checkout — plays the clip with audio before buying
// Arrow navigation only walks effects that actually have a clip — stepping into
// a "coming soon" placeholder is a dead end, not a preview.
const PLAYABLE_EFFECTS = KILL_EFFECTS.filter(effectVideo);

function stepEffect(current, dir) {
  if (PLAYABLE_EFFECTS.length === 0) return null;
  const at = PLAYABLE_EFFECTS.findIndex(e => e.key === current.key);
  // Opened from a card with no clip: enter the playable list at either end.
  if (at === -1) return PLAYABLE_EFFECTS[dir > 0 ? 0 : PLAYABLE_EFFECTS.length - 1];
  const next = (at + dir + PLAYABLE_EFFECTS.length) % PLAYABLE_EFFECTS.length;
  return PLAYABLE_EFFECTS[next];
}

function KillFxModal({ effect, ownedIndex, onClose, onNavigate, onOpenRank }) {
  const { user, requestLogin } = useAuth();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const owned = killEffectOwned(effect, ownedIndex);
  const rankLabel = effect.rank ? effect.rank.charAt(0).toUpperCase() + effect.rank.slice(1) : null;
  const rarity = rarityOf(effect);
  const videoUrl = effectVideo(effect);

  const canNavigate = PLAYABLE_EFFECTS.length > 1;
  const go = (dir) => {
    const next = stepEffect(effect, dir);
    if (next && next.key !== effect.key) onNavigate(next);
  };

  // Name the destination on the buttons so it's a browse control, not a mystery
  // arrow — and show the position so people know how much lineup is left.
  const playableAt = PLAYABLE_EFFECTS.findIndex(e => e.key === effect.key);
  const prevEffect = stepEffect(effect, -1);
  const nextEffect = stepEffect(effect, 1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, effect.key]);

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  async function handleCheckout() {
    if (!user) return;
    setIsCheckingOut(true);
    setCheckoutError("");
    try {
      const packageId = effect.tebex_id || KILLFX_TEBEX[effect.key];
      if (!packageId) throw new Error("This effect isn't available for purchase yet — check back soon.");
      const checkoutUrl = await createTebexCheckout(user.ign, packageId);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error(err);
      setCheckoutError(err.message || "An error occurred during checkout.");
      setIsCheckingOut(false);
    }
  }

  return (
    <div className="fx-lightbox-backdrop" onClick={handleBackdrop}>
      <div className="fx-lightbox" style={{ '--fx': effect.color }}>
        <button className="fx-lightbox-close" onClick={onClose} aria-label="Close preview">✕</button>

        <div className="fx-lightbox-frame">
          {videoUrl ? (
            // `key` forces a fresh element per effect so arrowing through
            // restarts playback instead of reusing the previous clip's state.
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video key={effect.key} src={videoUrl} controls autoPlay loop playsInline />
          ) : (
            <div className="fx-lightbox-novideo">Preview video coming soon</div>
          )}
        </div>

        <div className="fx-lightbox-bar">
          <div className="fx-lightbox-meta">
            <div className="fx-lightbox-name">{effect.name}</div>
            <div className="fx-lightbox-tags">
              <span className="fx-tag fx-tag--rarity" style={{ '--rar': rarity.color }}>{rarity.label}</span>
              {effect.rank
                ? (
                  <button
                    type="button"
                    className="fx-tag fx-tag--rank fx-tag--link"
                    onClick={() => { onClose(); onOpenRank?.(effect.rank); }}
                  >
                    Comes with {rankLabel} Rank
                  </button>
                )
                : <span className="fx-tag fx-tag--premium">Premium</span>}
              {effect.audio && <span className="fx-tag fx-tag--audio">Custom Audio</span>}
            </div>
          </div>

          {/* Browse controls live beside the action instead of in their own
              full-width row — one bar under the video, not two. */}
          {canNavigate && (
            <div className="fx-lightbox-steps">
              <button
                className="fx-step-btn"
                onClick={() => go(-1)}
                aria-label={`Previous effect: ${prevEffect?.name}`}
                title={prevEffect?.name}
              >‹</button>
              {playableAt >= 0 && (
                <span className="fx-step-count">{playableAt + 1} / {PLAYABLE_EFFECTS.length}</span>
              )}
              <button
                className="fx-step-btn"
                onClick={() => go(1)}
                aria-label={`Next effect: ${nextEffect?.name}`}
                title={nextEffect?.name}
              >›</button>
            </div>
          )}

          {owned ? (
            <span className="fx-btn fx-btn--owned fx-lightbox-action">✓ Unlocked with {rankLabel}</span>
          ) : !user ? (
            <button
              className="fx-btn fx-btn--buy fx-lightbox-action"
              onClick={() => { requestLogin(); onClose(); }}
            >
              Log in to buy · {fmtEur(effectPrice(effect))}
            </button>
          ) : canUseLiveStore(user) ? (
            <button
              className="fx-btn fx-btn--buy fx-lightbox-action"
              onClick={handleCheckout}
              disabled={isCheckingOut}
            >
              {isCheckingOut ? "Processing…" : `Buy · ${fmtEur(effectPrice(effect))}`}
            </button>
          ) : (
            <button className="fx-btn fx-btn--buy fx-lightbox-action" disabled style={{ opacity: 0.55, cursor: 'not-allowed' }}>
              Coming Soon
            </button>
          )}
        </div>

        {effect.rank && !owned && (
          <div className="fx-lightbox-note">
            Tip: this effect is included free with the{' '}
            <button
              type="button"
              className="fx-rank-link"
              style={{ '--rk': RANK_META[effect.rank]?.color || effect.color }}
              // Swap modals rather than stacking them — two overlays deep is a
              // maze, and the rank modal is now the thing being decided on.
              onClick={() => { onClose(); onOpenRank?.(effect.rank); }}
            >
              {rankLabel} Rank
            </button>.
          </div>
        )}
        {checkoutError && <div className="fx-lightbox-error">{checkoutError}</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   JOIN & LEAVE MESSAGES — individually sold. Permission node
   simplemc.joinmsg.<key>. Classic (default) is free.
   ═══════════════════════════════════════════════════════════════════ */
const JOIN_STYLES = [
  { key: 'default', name: 'Classic', accent: '#AAAAAA', price: 0,
    join: "&8[&a+&8] &7{player}", leave: "&8[&c-&8] &7{player}" },
  { key: 'clean', name: 'Clean', accent: '#55FF55', price: 0.99,
    join: "&a+ &7{player} &8joined the game", leave: "&c- &7{player} &8left the game" },
  { key: 'earth', name: 'Earthen', accent: '#7cba46', price: 0.99,
    join: "&#7cba46✦ &f{player} &7rooted in", leave: "&#7cba46✦ &f{player} &7withered away" },
  { key: 'wind', name: 'Gale', accent: '#8fd8e8', price: 0.99,
    join: "&#8fd8e8❋ &f{player} &7blew in", leave: "&#8fd8e8❋ &f{player} &7drifted off" },
  { key: 'water', name: 'Tidal', accent: '#4a9ee0', price: 0.99,
    join: "&#4a9ee0❋ &f{player} &7surfaced", leave: "&#4a9ee0❋ &f{player} &7sank beneath the waves" },
  { key: 'fire', name: 'Ember', accent: '#e8642c', price: 0.99,
    join: "&#e8642c🔥 &f{player} &7ignited the server", leave: "&#e8642c🔥 &f{player} &7burned out" },
  { key: 'storm', name: 'Storm', accent: '#f5d442', price: 0.99,
    join: "&#f5d442⚡ &f{player} &7struck in", leave: "&#f5d442⚡ &f{player} &7faded to static" },
  { key: 'royal', name: 'Royal', accent: '#f0ab20', price: 0.99,
    join: "&#f0ab20» &e{player} &7has arrived", leave: "&#f0ab20« &e{player} &7has taken leave" },
  { key: 'ghost', name: 'Wraith', accent: '#AAAAAA', price: 0.99,
    join: "&7≈ &f{player} &7materialized", leave: "&7≈ &f{player} &7vanished into mist" },
  { key: 'titan', name: 'Titan', accent: '#f0ab20', price: 0.99,
    join: "&#f0ab20✦ &l{player} &r&7has ascended", leave: "&#f0ab20✦ &l{player} &r&7has departed" },
  { key: 'legend', name: 'Legend', accent: '#c0392b', price: 0.99,
    join: "&#c0392b☠ &l{player} &r&7the Legend enters", leave: "&#c0392b☠ &l{player} &r&7the Legend departs" },
  { key: 'callout', name: 'Callout', accent: '#FFFF55', price: 0.99,
    join: "&e{player} &8» &f\"I'm in\"", leave: "&e{player} &8» &f\"I'm out\"" },
];

// Tebex package IDs per join-message style — fill in from the Creator Panel.
// Until populated, the buy button explains the style isn't live yet.
const JOINMSG_TEBEX = {};

function JoinMessageCard({ style, player, onBuy }) {
  const isFree = style.price === 0 || style.key === 'default';
  return (
    <div className="jm-card" style={{ '--jm': style.accent }}>
      <div className="jm-head">
        <span className="jm-name">{style.name}</span>
        {isFree
          ? <span className="jm-tag jm-tag--free">Free · Default</span>
          : <span className="jm-tag jm-tag--price">${style.price.toFixed(2)}</span>}
      </div>

      <div className="jm-node">simplemc.joinmsg.{style.key}</div>

      <div className="mc-chat">
        <div className="mc-chat-row">
          <span className="mc-chat-label">JOIN</span>
          <McChat text={style.join} player={player} />
        </div>
        <div className="mc-chat-row">
          <span className="mc-chat-label">LEAVE</span>
          <McChat text={style.leave} player={player} />
        </div>
      </div>

      {isFree ? (
        <button className="jm-btn jm-btn--free" disabled>✓ Default for everyone</button>
      ) : (
        <button className="jm-btn" onClick={() => onBuy(style)}>Buy · ${style.price.toFixed(2)}</button>
      )}
    </div>
  );
}

// Lightweight checkout modal for individually-sold cosmetics (join messages)
function CosmeticBuyModal({ item, player, onClose }) {
  const { user, requestLogin } = useAuth();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  async function handleCheckout() {
    if (!user) return;
    setIsCheckingOut(true);
    setCheckoutError("");
    try {
      const packageId = item.tebex_id || JOINMSG_TEBEX[item.key];
      if (!packageId) throw new Error("This style isn't available for purchase yet — check back soon.");
      const checkoutUrl = await createTebexCheckout(user.ign, packageId);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error(err);
      setCheckoutError(err.message || "An error occurred during checkout.");
      setIsCheckingOut(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div className="modal-box" style={{ '--rk': item.accent, '--rk2': item.accent }}>
        <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="modal-header">
          <div>
            <span className="modal-tier">Join Message</span>
            <div className="modal-rank-name">{item.name}</div>
            <div className="modal-price-row">
              <span className="rankx-price-now">${item.price.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="modal-perks-label">Preview</div>
        <div className="mc-chat" style={{ marginBottom: '1rem' }}>
          <div className="mc-chat-row">
            <span className="mc-chat-label">JOIN</span>
            <McChat text={item.join} player={player} />
          </div>
          <div className="mc-chat-row">
            <span className="mc-chat-label">LEAVE</span>
            <McChat text={item.leave} player={player} />
          </div>
        </div>

        <div className="jm-node" style={{ marginBottom: '1rem' }}>
          Unlocks <strong>simplemc.joinmsg.{item.key}</strong>
        </div>

        {!user ? (
          <div className="modal-login-gate">
            <p>Log in with your Minecraft account to buy this join message.</p>
            <button type="button" className="modal-buy-btn" onClick={() => { requestLogin(); onClose(); }}>
              Log In
            </button>
          </div>
        ) : (
          <>
            <div className="modal-buyer-self">
              <img src={user.avatar} alt={user.ign} className="modal-buyer-avatar" />
              <span>Buying for <strong>{user.ign}</strong></span>
            </div>
            {checkoutError && (
              <div style={{ color: 'var(--accent-bright)', fontSize: '13px', marginTop: '10px' }}>{checkoutError}</div>
            )}
            <div className="modal-actions" style={{ marginTop: '18px' }}>
              <button className="btn modal-cancel-btn" onClick={onClose} disabled={isCheckingOut}>Cancel</button>
              {canUseLiveStore(user) ? (
                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="modal-buy-btn"
                  style={{ opacity: isCheckingOut ? 0.7 : 1, cursor: isCheckingOut ? 'not-allowed' : 'pointer' }}
                >
                  {isCheckingOut ? "Processing..." : `Checkout · $${item.price.toFixed(2)}`}
                </button>
              ) : (
                <button className="modal-buy-btn" disabled style={{ opacity: 0.55, cursor: 'not-allowed' }}>
                  Coming Soon
                </button>
              )}
            </div>
          </>
        )}

        <p className="modal-disclaimer">
          Cosmetic purchases apply to your account and support server costs.
        </p>
      </div>
    </div>
  );
}

const STORE_TABS = [
  { key: 'effects', label: 'Kill Effects',  color: '#f0ab20' },
  { key: 'ranks',   label: 'Ranks',         color: '#e0562c' },
  { key: 'joins',   label: 'Join Messages', color: '#4a9ee0' },
];

export default function Ranks() {
  const { user } = useAuth();
  const [ranks, setRanks] = useState([]);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState(null);
  const [forceGift, setForceGift] = useState(false);
  const [tab, setTab] = useState('effects');
  const [selectedJoin, setSelectedJoin] = useState(null);
  const [previewFx, setPreviewFx] = useState(null);

  useEffect(() => {
    api("getranks").then((res) => {
      if (res.isSuccess && res.data) {
        setRanks(res.data.ranks ?? []);
        setSale(res.data.sale ?? null);
      }
      setLoading(false);
    });
  }, []);

  const shownRanks = ranks.length > 0 ? ranks : DEFAULT_RANKS;
  const ownedIndex = rankIndex(user?.rank);
  const ownedRank = shownRanks.find(r => rankIndex(r.name) === ownedIndex) || null;
  const ownedPrice = ownedRank ? Number(ownedRank.price) || 0 : 0;
  const playerName = user?.ign || "Steve";

  // A kill effect's rank credit is a sales path — let it open the rank's own
  // buy modal instead of leaving people to hunt for the tier themselves.
  function openRankModal(rankName) {
    const match = shownRanks.find(r => r.name.toLowerCase() === (rankName || "").toLowerCase());
    if (match) setSelectedRank(match);
  }

  function handleBuy(rank) {
    setForceGift(false);
    setSelectedRank(rank);
  }
  function handleGift(rank) {
    setForceGift(true);
    setSelectedRank(rank);
  }

  return (
    <>
      <PageHero
        video={HERO_VIDEO}
        eyebrow="Support the Server"
        title="SimpleMC "
        em="Store"
        subtitle="Ranks, kill effects and join messages — every purchase supports SimpleMC and unlocks exclusive perks and cosmetics in-game."
        height={"340px"}
      >
        <div className="ranks-hero-chips">
          {shownRanks.map(r => (
            <span key={r.id} className="ranks-hero-chip" style={{ '--rk': rankMeta(r).color }}>
              {r.name}
            </span>
          ))}
        </div>
      </PageHero>

      <div className="container section" style={{ paddingTop: '1.5rem' }}>
        <FadeIn duration={0.5}>
          {/* Switching sections is the main thing to do here, so the tabs lead
              rather than sitting under a notice most people don't need. */}
          <div className="store-tabs" role="tablist" aria-label="Store sections">
            {STORE_TABS.map(t => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`store-tab${tab === t.key ? ' active' : ''}`}
                style={{ '--tab': t.color }}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Only the join-message previews actually render your name, so this
              notice doesn't follow you around the other sections. */}
          {tab === 'joins' && (
            <div className="store-note">
              <span className="store-note-icon">🎮</span>
              <span>
                Previews use your Minecraft name when logged in
                {user ? <> — showing <strong>{playerName}</strong></> : <> — currently <strong>Steve</strong></>}.
              </span>
            </div>
          )}
        </FadeIn>

        {/* ── RANKS ─────────────────────────────────────────────── */}
        {tab === 'ranks' && (
          <>
            <FadeIn duration={0.6}>
              <div className="home-sec-head" style={{ marginTop: '1.5rem' }}>
                <span className="sec-eyebrow">Five elements, one choice</span>
                <h2 className="home-sec-title">Choose your <em>element</em></h2>
              </div>
            </FadeIn>

            {!loading && <ElementRail ranks={shownRanks} />}

            {loading ? (
              <div className="ranks-loading">Loading ranks…</div>
            ) : (
              <StaggerContainer className="rankx-grid">
                {shownRanks.map((rank) => (
                  <StaggerItem key={rank.id} className="rankx-card-wrap">
                    <RankCard
                      rank={rank}
                      onBuy={handleBuy}
                      onGift={handleGift}
                      ownedIndex={ownedIndex}
                      ownedPrice={ownedPrice}
                    />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            )}

            <FadeIn duration={0.6}>
              <div className="home-sec-head">
                <span className="sec-eyebrow">Side by side</span>
                <h2 className="home-sec-title">Compare <em>every perk</em></h2>
              </div>
            </FadeIn>

            <FadeIn duration={0.7}>
              <div className="rank-compare-wrap">
                <div className="rank-compare-table-scroll">
                  <table className="rank-compare-table">
                    <thead>
                      <tr>
                        <th>Features</th>
                        {shownRanks.map(r => {
                          const meta = rankMeta(r);
                          const badge = RANK_BADGES[r.name.toLowerCase()];
                          const isTitan = r.name.toLowerCase() === 'titan';
                          return (
                            <th key={r.id} className={isTitan ? 'compare-col-titan' : ''} style={{ color: meta.color }}>
                              <div className="rank-compare-header">
                                {badge && <span className={`rank-badge ${badge.className}`}>{badge.label}</span>}
                                <div className="rank-compare-orb" style={{ '--rk': meta.color }}>
                                  <McIcon materialKey={meta.material} size={26} />
                                </div>
                                {r.name}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {COMPARE_FEATURES.map(feat => (
                        <tr key={feat.label}>
                          <td>{feat.label}</td>
                          {shownRanks.map(r => {
                            const v = feat.values[r.name.toLowerCase()];
                            const isTitan = r.name.toLowerCase() === 'titan';
                            return (
                              <td key={r.id} className={isTitan ? 'compare-col-titan' : ''}>
                                {v === true
                                  ? <span style={{ color: rankMeta(r).color, fontWeight: 800 }}>✓</span>
                                  : v === false
                                    ? <span style={{ color: 'var(--text-muted)', opacity: 0.5 }}>✕</span>
                                    : (v ?? '-')}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </FadeIn>
          </>
        )}

        {/* ── KILL EFFECTS ──────────────────────────────────────── */}
        {tab === 'effects' && (
          <>
            <FadeIn duration={0.6}>
              <div className="home-sec-head" style={{ marginTop: '1.5rem' }}>
                <span className="sec-eyebrow">Premium PvP cosmetics</span>
                <h2 className="home-sec-title">Kill <em>effects</em></h2>
                <p className="store-sub">
                  Premium kill effects from {fmtEur(KILLFX_MIN_PRICE)} — priced by rarity. Click any
                  card to watch it in full with sound. A few come free with their matching rank. Log in to purchase.
                </p>
              </div>
            </FadeIn>

            <StaggerContainer className="fx-grid">
              {KILL_EFFECTS.map((effect) => (
                <StaggerItem key={effect.key} className="fx-card-wrap">
                  <KillEffectCard
                    effect={effect}
                    ownedIndex={ownedIndex}
                    onOpen={setPreviewFx}
                    onOpenRank={openRankModal}
                  />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </>
        )}

        {/* ── JOIN MESSAGES ─────────────────────────────────────── */}
        {tab === 'joins' && (
          <>
            <FadeIn duration={0.6}>
              <div className="home-sec-head" style={{ marginTop: '1.5rem' }}>
                <span className="sec-eyebrow">Announce yourself</span>
                <h2 className="home-sec-title">Join & leave <em>messages</em></h2>
                <p className="store-sub">
                  Live in-game previews rendered from Minecraft format codes.
                  Classic is free for everyone — grab a style below to stand out.
                </p>
              </div>
            </FadeIn>

            <StaggerContainer className="jm-grid">
              {JOIN_STYLES.map((style) => (
                <StaggerItem key={style.key} className="jm-card-wrap">
                  <JoinMessageCard style={style} player={playerName} onBuy={setSelectedJoin} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </>
        )}

        <div className="ranks-disclaimer card-raised">
          <img
            src={Icons.info}
            alt="Disclaimer"
            style={{ width: "30px" }}
            className="ranks-disclaimer-icon"
          />
          <p>
            Ranks and cosmetics are permanent and carry over between seasons unless stated
            otherwise. All purchases support server costs. By purchasing you
            agree to our terms. No refunds after the item has been applied.
          </p>
        </div>
      </div>

      {selectedRank && (
        <BuyModal
          rank={selectedRank}
          saleCode={sale?.active ? sale.code : null}
          onClose={() => setSelectedRank(null)}
          allRanks={shownRanks}
          forceGift={forceGift}
        />
      )}

      {selectedJoin && (
        <CosmeticBuyModal
          item={selectedJoin}
          player={playerName}
          onClose={() => setSelectedJoin(null)}
        />
      )}

      {previewFx && (
        <KillFxModal
          effect={previewFx}
          ownedIndex={ownedIndex}
          onClose={() => setPreviewFx(null)}
          onNavigate={setPreviewFx}
          onOpenRank={openRankModal}
        />
      )}
    </>
  );
}
